import { tokenValues } from '../../lib/token-value';
import { worldRoundRect } from '../../lib/home-map/world-canvas';

/**
 * The shared Symphony celebration burst.
 *
 * Extracted from MilestoneCard so a SECOND celebratory card (the credit gift) plays the identical
 * moment instead of growing its own. There is one burst in this product, kept 1:1 with the native
 * apps' `ConfettiBurst`; a second copy would drift from them the first time either was tuned.
 */

// The "Success Animation" confetti palette (Figma 12513-80548 particle set) — blue, green, lime, orange,
// cyan, pink, maroon, purple. Illustration content; kept 1:1 with the native apps' `confettiColors` /
// `MILESTONE_CONFETTI_COLORS`. Canvas takes a colour string, so these resolve via `tokenValues` at fire
// time — never a hex literal (see CLAUDE.md → Design tokens).
const CONFETTI_TOKENS = [
  'var(--ds-blue-600)', // blue #3B6BE1
  'var(--ds-green-success-700)', // green #276454
  'var(--ds-lime-500)', // lime #D6FF59
  'var(--ds-accent-03)', // orange #FF4E00
  'var(--ds-light-blue-300)', // cyan #96F4FF
  'var(--ds-pink-500)', // pink #FF9FFC
  'var(--ds-accent-05)', // maroon #81252C
  'var(--ds-accent-06)', // purple #9A75FF
];

const CONFETTI_COUNT = 31;

// `CanvasRenderingContext2D.roundRect` is missing from older Android system WebViews and pre-16.4
// Safari, where calling it threw `e.roundRect is not a function` out of the rAF loop and killed the
// whole burst mid-animation (Sentry ORION-WEB-3E). Trace the same shape with `arcTo`, through the
// helper the home map already uses for exactly this reason — one polyfill, not two.
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  worldRoundRect(ctx, x, y, w, h, r);
  ctx.fill();
}

/**
 * A one-shot canvas confetti burst: the Symphony particle shapes — filled circles, rounded squares and
 * rounded diamonds — pop gently OUT from `originEl`'s centre, fanned up and to the RIGHT (the reference
 * fan), scale in, ease out a touch further, then fade in place — no gravity. Self-stops after ~1.3s.
 * Kept 1:1 with the native `ConfettiBurst`.
 */
export function fireConfetti(canvas: HTMLCanvasElement, originEl: Element) {
  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return;
  }
  canvas.width = rect.width;
  canvas.height = rect.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  const palette = tokenValues(CONFETTI_TOKENS);
  const o = originEl.getBoundingClientRect();
  const ox = o.left - rect.left + o.width / 2;
  const oy = o.top - rect.top + o.height / 2;
  const n = CONFETTI_COUNT;
  const parts = Array.from({ length: n }, (_, i) => ({
    // The reference fan: as i grows, the angle sweeps from up-slightly-left round to the right, so the
    // burst reads as popping UP and to the RIGHT (not a symmetric ring).
    angle: -Math.PI / 2 + (i / Math.max(n - 1, 1)) * 1.9 - 0.15 + (Math.random() - 0.5) * 0.35,
    // Alternating near / far rings over a wide range, so the burst reads spread out rather than crowded.
    dist: ((i % 2 === 1 ? 72 : 118) + Math.random() * 78) * 0.62,
    size: 8 + Math.random() * 5,
    color: palette[i % palette.length],
    shape: i % 3,
    baseRot: (Math.random() - 0.5) * 0.6,
    delay: Math.random() * 0.08,
    dur: 0.72 + Math.random() * 0.24,
  }));
  let start = 0;
  const step = (now: number) => {
    if (start === 0) {
      start = now;
    }
    const t = (now - start) / 1000;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const p of parts) {
      const local = (t - p.delay) / p.dur;
      if (local < 0 || local > 1) {
        continue;
      }
      let r: number;
      let scale: number;
      let alpha: number;
      if (local < 0.28) {
        // quick pop OUT to the ring radius
        const f = 1 - Math.pow(1 - local / 0.28, 2);
        r = p.dist * f;
        scale = 0.35 + 0.65 * f;
        alpha = 1;
      } else {
        // fly outward, staying opaque, then a quick late fade
        const g = (local - 0.28) / 0.72;
        r = p.dist * (1 + 0.6 * g);
        scale = 1;
        const fadeT = Math.min(1, Math.max(0, (g - 0.65) / 0.35));
        alpha = 1 - fadeT * fadeT * (3 - 2 * fadeT);
      }
      const cx = ox + Math.cos(p.angle) * r;
      const cy = oy + Math.sin(p.angle) * r;
      const s = p.size * scale;
      ctx.save();
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.translate(cx, cy);
      ctx.fillStyle = p.color;
      if (p.shape === 0) {
        // filled circle
        ctx.beginPath();
        ctx.arc(0, 0, s / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 1) {
        // rounded square
        ctx.rotate(p.baseRot);
        roundRect(ctx, -s / 2, -s / 2, s, s, s * 0.22);
      } else {
        // rounded diamond (a rounded square turned 45°)
        ctx.rotate(Math.PI / 4 + p.baseRot);
        roundRect(ctx, -s * 0.42, -s * 0.42, s * 0.84, s * 0.84, s * 0.2);
      }
      ctx.restore();
    }
    if (t < 1.3) {
      requestAnimationFrame(step);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };
  requestAnimationFrame(step);
}


/** How many pieces fall in the end-of-setup rain, in two waves. Kept 1:1 with the native apps' `ConfettiRain`. */
export const CONFETTI_RAIN_COUNT = 140;
const RAIN_SECONDS = 3.2;

/**
 * The end-of-setup celebration (value-first onboarding `done`): confetti that falls from the top edge of
 * `canvas` across its whole width, in two waves. Each piece drifts sideways, sways and tumbles (a ribbon
 * flips by squashing its height), then fades out near the bottom. Same palette and shapes as the burst.
 */
export function fireConfettiRain(canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;
  const palette = tokenValues(CONFETTI_TOKENS);
  const parts = Array.from({ length: CONFETTI_RAIN_COUNT }, (_, i) => ({
    x: Math.random() * w,
    // Start above the top edge, so pieces enter the view rather than appearing in it.
    y: -20 - Math.random() * h * 0.25,
    // The second half is the second wave.
    delay: (i < CONFETTI_RAIN_COUNT / 2 ? 0 : 0.45) + Math.random() * 0.35,
    fall: h * (0.55 + Math.random() * 0.35),
    drift: (Math.random() - 0.5) * 120,
    sway: 10 + Math.random() * 18,
    swayRate: 2 + Math.random() * 3,
    spin: (Math.random() - 0.5) * 10,
    flipRate: 4 + Math.random() * 8,
    size: 7 + Math.random() * 6,
    color: palette[i % palette.length],
    shape: i % 4,
  }));
  let start = 0;
  const step = (now: number) => {
    if (start === 0) {
      start = now;
    }
    const t = (now - start) / 1000;
    ctx.clearRect(0, 0, w, h);
    for (const p of parts) {
      const local = t - p.delay;
      if (local < 0) {
        continue;
      }
      const life = local / (RAIN_SECONDS - p.delay);
      if (life > 1) {
        continue;
      }
      // Falls fast, then floats: gravity eased out, as paper meets air.
      const y = p.y + p.fall * (1 - Math.pow(1 - Math.min(1, local / 1.8), 2)) + local * 40;
      const x = p.x + p.drift * Math.min(1, local / 2) + Math.sin(local * p.swayRate) * p.sway;
      const alpha = life < 0.8 ? 1 : 1 - (life - 0.8) / 0.2;
      ctx.save();
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.translate(x, y);
      ctx.rotate(local * p.spin);
      ctx.fillStyle = p.color;
      const s = p.size;
      if (p.shape === 0) {
        ctx.beginPath();
        ctx.arc(0, 0, s / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 3) {
        // A ribbon, flipping over as it falls.
        ctx.scale(1, Math.cos(local * p.flipRate));
        roundRect(ctx, -s * 0.9, -s * 0.22, s * 1.8, s * 0.44, s * 0.1);
      } else {
        ctx.scale(1, p.shape === 1 ? Math.cos(local * p.flipRate) : 1);
        roundRect(ctx, -s / 2, -s / 2, s, s, s * 0.22);
      }
      ctx.restore();
    }
    if (t < RAIN_SECONDS) {
      requestAnimationFrame(step);
    } else {
      ctx.clearRect(0, 0, w, h);
    }
  };
  requestAnimationFrame(step);
}
